import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Environment } from '@cobliteam/react-environments';
import { type Camelized, camelize } from '@cobliteam/utils/camelize';
import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
import { ApiError } from './apiError';
import { authenticatedHeaders } from './authenticatedHeaders';
import { openTelemetryBaggageHeaders } from './baggage';

// Identifica o painel no field_usage do janus (AE-2555); o valor precisa estar na allowlist dele (AE-2557).
const GRAPHQL_CLIENT_NAME = 'herbie-dashboard';
const OPERATION_NAME = /\b(?:query|mutation|subscription)\s+([_A-Za-z]\w*)/;
const NOT_FOUND = 404;

type GraphQLError = {
  message: string;
  path?: readonly (string | number)[];
  extensions?: { code?: string; response?: { status?: number } };
};

type GraphQLResponse<TResult> = { data?: TResult | null; errors?: GraphQLError[] };

export type GraphQLRequestOptions = {
  signal?: AbortSignal;
  /** O 404 do resolver deixa o campo `null` em vez de rejeitar. */
  nullOnNotFound?: boolean;
  /** Com `data` e `errors` juntos, devolve o dado parcial em vez de rejeitar. */
  allowPartialData?: boolean;
  /** Repassado ao `camelize`, com os nomes originais em notação de ponto. */
  stopPaths?: readonly string[];
};

type TypedDocument<TResult, TVariables> = DocumentTypeDecoration<TResult, TVariables> & { toString(): string };

// EventSource e mensagem seguem o GraphQLRequester, para os monitores do Datadog continuarem casando.
const report = (error: ApiError, context: object = {}) => {
  DatadogTrack.error(error, {
    operationName: error.operation,
    httpStatus: error.status,
    code: error.code,
    EventSource: 'GraphQLRequester',
    client: '@cobliteam/api',
    fingerprint: error.message,
    ...context,
  });
};

const readBody = async <TResult>(response: Response, signal: AbortSignal | undefined): Promise<GraphQLResponse<TResult>> => {
  try {
    return (await response.json()) as GraphQLResponse<TResult>;
  } catch (cause) {
    if (signal?.aborted) throw cause;
    return {};
  }
};

/**
 * Executa um documento do codegen no janus e devolve o `data` em camelCase. `errors` vira
 * `ApiError`, a não ser que a operação declare `nullOnNotFound` ou `allowPartialData`. O
 * cancelamento pelo `signal` rejeita com o próprio `AbortError`, que o TanStack Query reconhece.
 */
export const requestGraphQL = async <TResult, TVariables>(
  document: TypedDocument<TResult, TVariables>,
  variables: TVariables,
  options: GraphQLRequestOptions = {}
): Promise<Camelized<TResult>> => {
  const query = document.toString();
  const operation = OPERATION_NAME.exec(query)?.[1];
  const headers = {
    ...(await authenticatedHeaders(operation, (error) => report(error))),
    ...openTelemetryBaggageHeaders(),
    'apollographql-client-name': GRAPHQL_CLIENT_NAME,
    'content-type': 'application/json',
  };

  let response: Response;
  try {
    response = await fetch(Environment.getInstance().getEnvironment().graphqlHost, {
      method: 'POST',
      headers,
      body: JSON.stringify({ operationName: operation, variables, query }),
      signal: options.signal,
    });
  } catch (cause) {
    if (options.signal?.aborted) throw cause;
    const error = new ApiError({ message: `Network error in operation ${operation} - ${(cause as Error).message}`, origin: 'graphql', operation, cause });
    report(error);
    throw error;
  }

  if (!response.ok) {
    const error = new ApiError({
      message: `Network error in operation ${operation} - Received status code ${response.status}`,
      origin: 'graphql',
      status: response.status,
      operation,
    });
    report(error);
    throw error;
  }

  const { data, errors = [] } = await readBody<TResult>(response, options.signal);

  if (errors.length > 0) {
    const [first] = errors;
    const notFound = errors.every((error) => error.extensions?.response?.status === NOT_FOUND);
    const error = new ApiError({
      message: `GraphQL error in operation ${operation} - ${first.message}`,
      origin: 'graphql',
      status: first.extensions?.response?.status,
      code: first.extensions?.code,
      operation,
      cause: errors,
    });
    // O errorLink de hoje não reporta 404, que em geral é um dado que não existe.
    if (!notFound) report(error, { graphQLErrors: errors });
    const tolerated = (notFound && options.nullOnNotFound) || (options.allowPartialData && data != null);
    if (!tolerated) throw error;
  }

  if (data == null) throw new ApiError({ message: `GraphQL error in operation ${operation} - response without data`, origin: 'graphql', operation });
  return camelize(data as Record<string, unknown>, { stopPaths: options.stopPaths }) as Camelized<TResult>;
};
